// Phase 40 Plan 03 Task 2 — DeadLetterModal (UI-SPEC §12).
//
// Half-sheet on mobile / centred modal on tablet+. Lists ops that failed to
// sync after 3 retries (D-14), each with Try again / Discard buttons + bulk
// "Try all again" / "Discard all" actions.
//
// Props:
//   ops:           Op[]   — entries from tend:wal-deadletter
//   onTryAgain:    (op) => void
//   onDiscard:     (op) => void
//   onTryAll:      () => void
//   onDiscardAll:  () => void
//   onClose:       () => void

import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

function summariseOp(op) {
  const verb = op.op === 'insert' ? 'Add' : op.op === 'update' ? 'Edit' : 'Remove';
  const target = op.payload?.name || op.cloudId || op.table;
  // singularise common table names — UK English copy.
  const t = op.table === 'plants' ? 'plant' : op.table === 'areas' ? 'area' : op.table;
  return `${verb} ${t} ${target}`;
}

function reasonLabel(reason) {
  if (!reason) return 'Unknown reason';
  if (reason === 'free_tier_plant_cap_exceeded') return 'Free plant limit reached';
  if (reason === 'network_error') return 'Network error';
  if (reason.startsWith('HTTP 5')) return 'Cloud server error';
  if (reason.startsWith('HTTP 4')) return 'Cloud rejected the change';
  return reason;
}

function formatTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

export default function DeadLetterModal({
  ops = [],
  onTryAgain = () => {},
  onDiscard = () => {},
  onTryAll = () => {},
  onDiscardAll = () => {},
  onClose = () => {},
}) {
  const [confirmDiscardAll, setConfirmDiscardAll] = useState(false);
  const n = ops.length;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'var(--c-overlay)', zIndex: 2100,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dead-letter-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--c-bg)', borderTopLeftRadius: 18, borderTopRightRadius: 18,
          padding: 24, width: '100%', maxWidth: 480, maxHeight: '85vh',
          overflowY: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.2)',
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', cursor: 'pointer', color: 'var(--c-text-3)',
            padding: 4, borderRadius: 6, lineHeight: 1, display: 'flex',
          }}
        >
          <X size={20} />
        </button>

        <h2
          id="dead-letter-title"
          style={{
            fontFamily: "'Playfair Display', serif", fontSize: 'var(--font-h3)',
            color: 'var(--c-heading)', margin: '0 0 8px', fontWeight: 700,
            display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <AlertTriangle size={20} style={{ color: 'var(--c-overdue-text)' }} />
          <span><em>{n}</em> changes couldn't sync</span>
        </h2>

        <p style={{ fontSize: 'var(--font-body)', color: 'var(--c-text)', margin: '0 0 16px', lineHeight: 1.5 }}>
          These changes are saved on this device but didn't reach the cloud.
          The most common reason is that you're at the free plant limit (25).
          Upgrade or remove some plants, then try again.
        </p>

        {/* Bulk actions */}
        {n > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            <button
              type="button"
              onClick={onTryAll}
              style={{
                background: 'var(--c-brand-deep)', color: 'var(--c-accent-text)',
                border: 'none', borderRadius: 22, padding: '10px 16px',
                fontSize: 'var(--font-body)', fontWeight: 600, cursor: 'pointer',
                width: '100%', minHeight: 44, fontFamily: 'inherit',
              }}
            >
              Try all again
            </button>
            {!confirmDiscardAll ? (
              <button
                type="button"
                onClick={() => setConfirmDiscardAll(true)}
                style={{
                  background: 'var(--c-bg)', color: 'var(--c-danger)',
                  border: '1px solid var(--c-danger)', borderRadius: 22,
                  padding: '10px 16px', fontSize: 'var(--font-body)', fontWeight: 600,
                  cursor: 'pointer', width: '100%', minHeight: 44,
                  fontFamily: 'inherit',
                }}
              >
                Discard all
              </button>
            ) : (
              <div style={{
                background: 'var(--c-danger-bg)', borderRadius: 12,
                padding: 14, display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <small style={{ color: 'var(--c-danger)', fontSize: 'var(--font-small)' }}>
                  Discard all <em>{n}</em> unsynced changes? This can't be undone.
                </small>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={onDiscardAll}
                    style={{
                      background: 'var(--c-danger)', color: 'var(--c-danger-text)',
                      border: 'none', borderRadius: 18, padding: '8px 14px',
                      fontSize: 'var(--font-small)', fontWeight: 600, cursor: 'pointer',
                      flex: 1, fontFamily: 'inherit',
                    }}
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDiscardAll(false)}
                    style={{
                      background: 'var(--c-btn-bg)', color: 'var(--c-btn-text)',
                      border: 'none', borderRadius: 18, padding: '8px 14px',
                      fontSize: 'var(--font-small)', fontWeight: 600, cursor: 'pointer',
                      flex: 1, fontFamily: 'inherit',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Per-row dead-letter ops */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ops.map((op) => (
            <div
              key={`${op.ts}|${op.cloudId}|${op.op}`}
              style={{
                background: 'var(--c-surface)', borderRadius: 12, padding: 14,
              }}
            >
              <h3 style={{
                margin: 0, fontSize: 'var(--font-body)', fontWeight: 600, color: 'var(--c-heading)',
              }}>
                {summariseOp(op)}
              </h3>
              <small style={{ color: 'var(--c-text-2)', fontSize: 'var(--font-tiny)', display: 'block', marginTop: 2 }}>
                {formatTime(op.deadLetterAt || op.ts)} · {reasonLabel(op.deadLetterReason)}
              </small>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => onTryAgain(op)}
                  style={{
                    background: 'var(--c-btn-bg)', color: 'var(--c-btn-text)',
                    border: 'none', borderRadius: 18, padding: '6px 14px',
                    fontSize: 'var(--font-small)', fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={() => onDiscard(op)}
                  style={{
                    background: 'var(--c-bg)', color: 'var(--c-danger)',
                    border: '1px solid var(--c-danger-border)', borderRadius: 18,
                    padding: '6px 14px', fontSize: 'var(--font-small)', fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Discard
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
